import { createWebSupportHeaderEventFactory } from '@spotify-internal/ubi-sdk-support-spotify-com-web-support-header';
import { useMemo } from 'react';

import { MastheadContainer } from 'components/Masthead/Header/types';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';

import { useUbiContext } from '../ubiProvider';

const isInternalLink = (uri: string) => uri.startsWith('/') || uri.includes('spotify.com');

export const useUbiMastheadHeader = () => {
  const ubiLogger = useUbiContext();
  const {
    verticalConfig: { verticalName },
  } = useServerSideConstants();
  const headerContainer = useMemo(
    () =>
      createWebSupportHeaderEventFactory({
        data: {
          identifier: verticalName,
        },
      }),
    [verticalName],
  );

  return useMemo(
    () => ({
      logAvatarInteraction: (expanded: boolean) => {
        if (expanded) {
          ubiLogger.logInteraction(headerContainer.headerContainerFactory().headerAvatarFactory().hitUiReveal());
        } else {
          ubiLogger.logInteraction(headerContainer.headerContainerFactory().headerAvatarFactory().hitUiHide());
        }
      },
      logProfileLinksContainerImpression: (container: MastheadContainer) =>
        ubiLogger.logImpression(
          headerContainer
            .headerContainerFactory()
            .headerProfileLinksContainerFactory({ identifier: container })
            .impression(),
        ),
      logProfileLinkInteraction: ({
        identifier,
        uri,
        container,
      }: {
        identifier: string;
        uri: string;
        container: MastheadContainer;
      }) => {
        const urlLinkInstance = headerContainer
          .headerContainerFactory()
          .headerProfileLinksContainerFactory({ identifier: container })
          .headerProfileLinkFactory({
            identifier,
            uri,
          });
        if (isInternalLink(uri)) {
          ubiLogger.logInteraction(urlLinkInstance.hitUiNavigate({ destination: uri }));
        } else {
          // TODO: update UBI spec
          // ubiLogger.logInteraction(urlLinkInstance.hitNavigateToExternalUri({ destination: uri }));
        }
      },
    }),
    [headerContainer, ubiLogger],
  );
};

export type UbiMastheadHeader = ReturnType<typeof useUbiMastheadHeader>;
